import React, { ReactNode, useId } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { ButtonProps } from '@mui/material/Button';

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  onConfirm: () => void;
  onClose?: () => void; // fired when dialog requests close (backdrop, X, cancel)
  onCancel?: () => void; // explicit cancel button click
  confirmText?: string;
  cancelText?: string;
  confirmColor?: ButtonProps['color'];
  confirmSx?: ButtonProps['sx'];
  confirmVariant?: ButtonProps['variant'];
  cancelVariant?: ButtonProps['variant'];
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  disableAutoFocus?: boolean;
  disableRestoreFocus?: boolean;
  processing?: boolean; // disable actions during processing
  showCloseIcon?: boolean;
  confirmDisabled?: boolean;
  responsiveLayout?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  onConfirm,
  onClose,
  onCancel,
  confirmText = '実行',
  cancelText = 'キャンセル',
  confirmColor = 'primary',
  confirmSx,
  confirmVariant = 'contained',
  cancelVariant = 'text',
  fullWidth = true,
  maxWidth = 'xs',
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  disableAutoFocus = false,
  disableRestoreFocus = false,
  processing = false,
  showCloseIcon = false,
  confirmDisabled = false,
  responsiveLayout = false,
}) => {
  const titleId = useId();
  const descriptionId = useId();

  const requestClose = () => {
    if (onClose) onClose();
    else if (onCancel) onCancel();
  };

  const handleClose = () => {
    if (processing) return;
    requestClose();
  };

  const handleCancel = () => {
    if (processing) return;
    if (onCancel && onCancel !== onClose) onCancel();
    requestClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') return;
        if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
        handleClose();
      }}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableAutoFocus={disableAutoFocus}
      disableRestoreFocus={disableRestoreFocus}
      aria-labelledby={titleId}
      aria-describedby={description !== undefined && description !== null ? descriptionId : undefined}
      PaperProps={responsiveLayout ? {
        sx: {
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          maxWidth: 520,
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          borderRadius: 3,
          overflow: 'visible',
          position: 'relative',
        },
      } : undefined}
    >
      <DialogTitle
        id={titleId}
        sx={{
          display: responsiveLayout ? 'block' : 'flex',
          alignItems: responsiveLayout ? undefined : 'center',
          pr: responsiveLayout ? undefined : (showCloseIcon ? 1 : 2),
          ...(responsiveLayout ? {
            px: { xs: 2.5, sm: 3.5 },
            pr: showCloseIcon ? { xs: 7, sm: 8 } : { xs: 2.5, sm: 3.5 },
            pt: { xs: 2.25, sm: 2.5 },
            pb: { xs: 1.25, sm: 1.5 },
            fontSize: { xs: '1.2rem', sm: '1.35rem' },
            fontWeight: 700,
            lineHeight: 1.4,
            minHeight: 0,
            flex: '0 0 auto',
          } : {}),
        }}
      >
        <span style={{ flex: 1 }}>{title}</span>
        {showCloseIcon && (
          <IconButton
            aria-label="閉じる"
            onClick={handleClose}
            edge={responsiveLayout ? false : 'end'}
            size="small"
            disabled={processing}
            sx={responsiveLayout ? {
              position: 'absolute',
              top: -14,
              right: -14,
              zIndex: 1,
              // 週報画面の小型アイコン向け共通CSSによる上余白を、このダイアログでは無効化する。
              m: '0 !important',
              width: 36,
              height: 36,
              color: '#fff',
              backgroundColor: '#172033',
              '&:hover': { backgroundColor: '#29344a' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)', backgroundColor: '#667085' },
            } : undefined}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      {description !== undefined && description !== null && (
        <DialogContent
          id={descriptionId}
          sx={responsiveLayout ? {
            px: { xs: 2.5, sm: 3.5 },
            pt: '4px !important',
            pb: { xs: 2.5, sm: 3 },
          } : undefined}
        >
          {typeof description === 'string' ? (
            <DialogContentText>{description}</DialogContentText>
          ) : (
            description
          )}
        </DialogContent>
      )}

      <DialogActions
        disableSpacing={responsiveLayout}
        sx={responsiveLayout ? {
          px: { xs: 2.5, sm: 3.5 },
          pb: { xs: 2.5, sm: 3.5 },
          pt: 0,
          gap: { xs: 1.25, sm: 1.5 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& .MuiButton-root': {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 144 },
            minHeight: 44,
            m: 0,
            borderRadius: 999,
            fontWeight: 700,
          },
        } : undefined}
      >
        <Button onClick={handleCancel} disabled={processing} variant={cancelVariant}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          sx={confirmSx}
          variant={confirmVariant}
          disabled={processing || confirmDisabled}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
